import { useEffect, useMemo, useState } from 'react'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox'
import { useTranslation } from 'react-i18next'
import { filterIso7010PictogramCodes } from './filterIso7010PictogramCodes'
import {
  getIso7010PictogramUrl,
  ISO_7010_PICTOGRAM_CODES,
} from './iso7010Pictograms'

function getPictogramLabel(
  code: string,
  translate: (key: string) => string
): string {
  const name = translate(`${code}.name`)
  return name !== `${code}.name` ? name : code
}

export type PictogramSelectProps = {
  value: string | null
  onChange: (value: string | null) => void
  disabled?: boolean
  id?: string
  placeholder?: string
  searchPlaceholder: string
  noResultsText: string
}

export function PictogramSelect({
  value,
  onChange,
  disabled = false,
  id,
  placeholder: _placeholder = 'None',
  searchPlaceholder,
  noResultsText,
}: PictogramSelectProps) {
  const { t } = useTranslation('pictograms')
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const getLabel = useMemo(
    () => (code: string) => getPictogramLabel(code, t),
    [t]
  )

  const pictogramCodes = useMemo(() => [...ISO_7010_PICTOGRAM_CODES], [])

  const filteredCodes = useMemo(
    () => filterIso7010PictogramCodes(inputValue, pictogramCodes, getLabel),
    [inputValue, pictogramCodes, getLabel]
  )

  const hasSelectedPictogram =
    value != null && value.trim() !== ''

  useEffect(() => {
    if (hasSelectedPictogram) {
      setInputValue(getLabel(value))
    }
  }, [hasSelectedPictogram, value, getLabel])

  function syncInputToSelection() {
    if (hasSelectedPictogram) {
      setInputValue(getLabel(value))
      return
    }
    setInputValue('')
  }

  return (
    <Combobox
      disabled={disabled}
      items={pictogramCodes}
      filteredItems={filteredCodes}
      filter={null}
      autoHighlight={false}
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (!nextOpen) {
          syncInputToSelection()
        }
      }}
      inputValue={inputValue}
      onInputValueChange={(next) => {
        setInputValue(next ?? '')
      }}
      value={hasSelectedPictogram ? value : null}
      onValueChange={(code) => {
        if (code == null) {
          onChange(null)
          setInputValue('')
          return
        }
        onChange(code)
        setInputValue(getLabel(code))
        setOpen(false)
      }}
      isItemEqualToValue={(left, right) => left === right}
      itemToStringLabel={(code) => getLabel(code)}
    >
      <ComboboxInput
        id={id}
        autoComplete='off'
        className='w-full'
        disabled={disabled}
        placeholder={searchPlaceholder}
        showClear={hasSelectedPictogram}
        showTrigger={false}
      />
      <ComboboxContent className='w-(--anchor-width) min-w-55 p-0'>
        <ComboboxList>
          <ComboboxEmpty>{noResultsText}</ComboboxEmpty>
          {filteredCodes.map((code) => (
            <ComboboxItem key={code} value={code}>
              <span className='flex items-center gap-2'>
                <img
                  src={getIso7010PictogramUrl(code)}
                  alt=""
                  className='size-6 object-contain'
                />
                {getLabel(code)}
              </span>
            </ComboboxItem>
          ))}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}
